"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Live: die Ansicht frischt sich selbst auf
// ============================================================
// Haengt im Rahmen (Layout), nicht an einer Seite: das Layout
// bleibt beim Seitenwechsel stehen, die Leitung wird also EINMAL
// je Reiter aufgebaut und nicht bei jedem Sprung neu.
//
// Der ganze Mechanismus ist ein Klopfzeichen plus `router.refresh()`:
// Next zeichnet die Server-Komponenten der aktuellen Adresse neu und
// tauscht sie aus. Was im Browser lebt, ueberlebt das — ein offener
// Dialog bleibt offen, ein halb getippter Satz bleibt stehen, der
// Fokus bleibt im Feld (am 22.09.2026 nachgemessen). Darum darf das
// oft passieren, ohne jemanden bei der Arbeit zu stoeren.
//
// Vier Kanten halten es ruhig:
// - ABKLINGZEIT: zehn Schreibvorgaenge in einer Sekunde (Import,
//   Abgleich, Sammelrechnung) ergeben EINE Auffrischung. Sie laeuft
//   nach: jedes neue Ereignis schiebt sie weiter, aber nie ueber
//   HOECHSTWARTEZEIT hinaus — ein langer Import zeigt so trotzdem
//   Zwischenstaende, statt bis zum Ende stillzustehen.
// - MINDESTABSTAND: auch ein Dauerfeuer frischt hoechstens alle
//   anderthalb Sekunden auf.
// - EINE AUF EINMAL: laeuft noch eine Auffrischung, wird die naechste
//   nur vorgemerkt. Next reiht `router.refresh()` sonst hintereinander
//   ein — unter Last liefen so noch Minuten nach dem letzten
//   Schreibvorgang Auffrischungen weiter (Log vom 24.09.2026).
// - WER NICHT HINSIEHT, KRIEGT NICHTS: im verdeckten Reiter wird
//   nur gemerkt, dass etwas anliegt — geholt wird es beim
//   Zurueckkommen. Sonst rechnet ein vergessenes Fenster in einer
//   Ecke des Bildschirms den ganzen Tag Seiten nach.
//
// Das Sicherheitsnetz darunter: reisst die Leitung und kommt sie
// nicht zurueck, wird einmal pro Minute trotzdem geholt. Eine
// stehengebliebene Ansicht ist schlimmer als eine Abfrage zu viel.
//
// NEUER BAU (seit 26.09.2026): agent.ansichtssache.de wird nach
// jeder Aenderung neu gebaut und ausgetauscht (`bin/live-deploy.sh`).
// Trifft ein Reiter danach auf den neuen Bau, laedt Next bei der
// naechsten Auffrischung die GANZE Seite neu — mitten in einen halb
// getippten Satz hinein. Deshalb meldet `/api/live` beim Verbinden
// seine Kennung; weicht sie von der eigenen ab, frischt der Reiter
// nicht mehr auf, sondern laedt neu, sobald niemand darin schreibt
// und kein Dialog offen ist. Im Entwicklungsserver gibt es keine
// Kennung, dort bleibt alles beim Alten.
//
// `useLive` ist fuer die Ansichten, die `router.refresh()` NICHT
// erreicht: wer seine Daten im Browser haelt (die Glocke holt ihre
// Meldungen selbst), haengt sich hier an — an DIESELBE Leitung, statt
// eine zweite zu oeffnen oder im Takt zu pollen.
// ============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useTransition,
} from "react";
import { useRouter } from "next/navigation";
import { isListenerOnly } from "@/lib/live/modelle";
import type { LiveEreignis } from "@/lib/live/typen";

const ABKLINGZEIT_MS = 400;
const HOECHSTWARTEZEIT_MS = 3_000;
// Alle Reiter bekommen ein Ereignis in derselben Millisekunde. Ohne
// Streuung fragen sie nach genau 400 ms alle zugleich beim Server an.
const STREUUNG_MS = 300;
const MINDESTABSTAND_MS = 1_500;
const NETZ_MS = 60_000;
const NEUAUFBAU_MAX_MS = 30_000;
// Wie oft ein veralteter Reiter nachsieht, ob er jetzt neu laden darf.
const NEULADEN_PRUEFEN_MS = 1_500;

// Der Bau, aus dem DIESES Browserbuendel stammt — beim Bauen
// eingesetzt. Im Entwicklungsserver leer.
const EIGENER_BAU = process.env.NEXT_PUBLIC_AGENT_BUILD;

/**
 * Darf der Reiter jetzt neu laden, ohne dass jemandem etwas verloren
 * geht? Ja, wenn er verdeckt ist — oder wenn weder ein Feld den Fokus
 * hat noch ein Dialog offen ist. Ein Inline-Feld speichert beim
 * Verlassen; solange es den Fokus hat, ist sein Inhalt nur im Browser.
 */
function darfNeuLaden(): boolean {
  if (document.visibilityState !== "visible") return true;
  if (document.querySelector('[aria-modal="true"]')) return false;
  const fokus = document.activeElement as HTMLElement | null;
  if (!fokus) return true;
  if (fokus.isContentEditable) return false;
  return !fokus.matches(
    'textarea, select, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])',
  );
}

type Horcher = (ereignis: LiveEreignis) => void;

type LiveContextValue = {
  subscribe: (horcher: Horcher) => () => void;
  /** Siehe `useLiveCover`. */
  cover: () => void;
  /** Siehe `LiveRefreshOn`: eine Auffrischung anstossen, mit allen Kanten. */
  request: () => void;
};

const LiveKontext = createContext<LiveContextValue | null>(null);

export function LiveProvider({
  children,
  source = "/api/live",
}: {
  children: React.ReactNode;
  /**
   * Die Leitung. Im Haus und im Kundenbereich `/api/live`; eine
   * Gastseite ohne Konto haengt `?gast=<bereich>:<token>` an (siehe
   * `GastLive`). `null`: noch keine Leitung — die Gastsitzung steht
   * noch aus.
   */
  source?: string | null;
}) {
  const router = useRouter();

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zuletzt = useRef(0);
  const nachholen = useRef(false);
  const verbunden = useRef(false);
  const horcher = useRef<Set<Horcher>>(new Set());
  // Seit wann Ereignisse auf eine Auffrischung warten (HOECHSTWARTEZEIT).
  const ersteWartende = useRef<number | null>(null);
  // Kam etwas, waehrend noch aufgefrischt wurde?
  const vorgemerkt = useRef(false);
  const laeuft = useRef(false);
  const anstossenRef = useRef<(() => void) | null>(null);
  const [auffrischungLaeuft, startAuffrischung] = useTransition();

  useEffect(() => {
    laeuft.current = auffrischungLaeuft;
    if (!auffrischungLaeuft && vorgemerkt.current) {
      vorgemerkt.current = false;
      anstossenRef.current?.();
    }
  }, [auffrischungLaeuft]);

  // Eine Ansicht hat nach einer eigenen Action selbst aufgefrischt.
  // Das Ereignis dieser Action liegt dann meist schon hier und hat
  // eine zweite Auffrischung eingeplant — dieselbe Arbeit noch einmal.
  // Was vor dem eigenen Auffrischen ankam, steckt in dessen Stand.
  const cover = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    ersteWartende.current = null;
    zuletzt.current = Date.now();
  }, []);

  // Anmelden am Strom. Der Rueckgabewert meldet wieder ab — ohne ihn
  // bleibt nach jeder ausgehaengten Ansicht ein Horcher liegen.
  const anmelden = useCallback((neuer: Horcher) => {
    horcher.current.add(neuer);
    return () => {
      horcher.current.delete(neuer);
    };
  }, []);

  useEffect(() => {
    // Noch keine Leitung (Gastseite vor der Sitzung): auch kein
    // Minutennetz — ohne Sitzung wuerde jedes Auffrischen als
    // neuer Aufruf zaehlen.
    if (!source) return;
    const adresse = source;

    // Laeuft der Reiter auf einem alten Bau, holt `router.refresh()`
    // nicht mehr die Ansicht, sondern laedt die ganze Seite neu (Next
    // vergleicht die Baukennung). Dann lieber selbst neu laden, und
    // zwar in einem Moment, in dem es niemandem etwas wegnimmt.
    let veraltet = false;
    let neuladenUhr: ReturnType<typeof setInterval> | null = null;
    const neuLadenWennSicher = () => {
      if (darfNeuLaden()) {
        window.location.reload();
        return;
      }
      neuladenUhr ??= setInterval(() => {
        if (darfNeuLaden()) window.location.reload();
      }, NEULADEN_PRUEFEN_MS);
    };

    const auffrischen = () => {
      timer.current = null;
      ersteWartende.current = null;
      if (veraltet) {
        neuLadenWennSicher();
        return;
      }
      if (laeuft.current) {
        vorgemerkt.current = true;
        return;
      }
      nachholen.current = false;
      zuletzt.current = Date.now();
      startAuffrischung(() => router.refresh());
    };

    const anstossen = () => {
      if (document.visibilityState !== "visible") {
        nachholen.current = true;
        return;
      }
      const jetzt = Date.now();
      ersteWartende.current ??= jetzt;
      // Nachlaufend: ein neues Ereignis schiebt die Auffrischung
      // weiter, bis HOECHSTWARTEZEIT erreicht ist.
      if (timer.current) clearTimeout(timer.current);
      const rest = HOECHSTWARTEZEIT_MS - (jetzt - ersteWartende.current);
      const wartezeit = Math.max(
        Math.min(ABKLINGZEIT_MS + Math.random() * STREUUNG_MS, rest),
        MINDESTABSTAND_MS - (jetzt - zuletzt.current),
        0,
      );
      timer.current = setTimeout(auffrischen, wartezeit);
    };
    anstossenRef.current = anstossen;

    // `EventSource` verbindet nach einem Abriss von selbst neu —
    // aber nicht, wenn der Server beim Verbinden mit etwas anderem
    // als 200 antwortet (502 waehrend eines Neustarts, 401 nach
    // Ablauf der Sitzung). Dann ist sie endgueltig zu, und der Reiter
    // hinge fuer immer am Minutennetz. Das holt `verbinden` nach.
    let quelle: EventSource | null = null;
    let neuaufbau: ReturnType<typeof setTimeout> | null = null;
    let versuch = 0;
    // War die Leitung schon einmal offen? Dann ist jedes weitere
    // `onopen` ein Wiederverbinden, nach dem nachgeholt wird.
    let warVerbunden = false;

    const verbinden = () => {
      neuaufbau = null;
      const neue = new EventSource(adresse);
      quelle = neue;

      neue.onopen = () => {
        versuch = 0;
        // Beim ERSTEN Verbinden nicht auffrischen — die Seite ist
        // gerade erst gekommen. Beim zweiten schon: dazwischen kann
        // etwas passiert sein, das niemand mehr nachliefert. (Bis zum
        // 25.09.2026 setzte `onerror` den Merker zurueck, und genau
        // dieses Nachholen fiel dadurch immer aus.)
        if (warVerbunden) anstossen();
        warVerbunden = true;
        verbunden.current = true;
      };

      // Kommt vor jedem anderen Ereignis und vor dem Nachholen aus
      // `onopen` (das wartet ABKLINGZEIT_MS) — ein Reiter, der nach dem
      // Austausch der Instanz neu verbindet, laedt also neu, statt
      // aufzufrischen.
      neue.addEventListener("build", (nachricht) => {
        const bau = (nachricht as MessageEvent<string>).data;
        if (!EIGENER_BAU || !bau || bau === EIGENER_BAU || veraltet) return;
        veraltet = true;
        neuLadenWennSicher();
      });

      neue.onerror = () => {
        verbunden.current = false;
        if (neue.readyState !== EventSource.CLOSED) return;
        // Endgueltig zu: selbst neu aufbauen, mit wachsendem Abstand.
        // Bis dahin greift das Minutennetz.
        neue.close();
        neuaufbau = setTimeout(verbinden, Math.min(NEUAUFBAU_MAX_MS, 2_000 * 2 ** versuch++));
      };

      neue.onmessage = (nachricht) => {
        // Die Ansichten, die ihre Daten selbst halten. Ein Fehler in
        // einer darf die anderen nicht mitnehmen.
        let ereignis: LiveEreignis;
        try {
          ereignis = JSON.parse(nachricht.data) as LiveEreignis;
        } catch {
          anstossen();
          return;
        }
        // Was nur Horcher betrifft (Glocke, Geraete), zeichnet keine
        // Seite auf dem Server — dafuer muss nicht jeder Reiter jedes
        // Menschen im Haus seine Seite neu holen.
        if (!isListenerOnly(ereignis.modell)) anstossen();
        for (const einer of horcher.current) {
          try {
            einer(ereignis);
          } catch {
            // Die Ansicht ist vermutlich schon weg.
          }
        }
      };
    };
    verbinden();

    const beiSichtwechsel = () => {
      if (document.visibilityState === "visible" && nachholen.current) anstossen();
    };
    document.addEventListener("visibilitychange", beiSichtwechsel);

    const netz = setInterval(() => {
      if (!verbunden.current && document.visibilityState === "visible") anstossen();
    }, NETZ_MS);

    return () => {
      quelle?.close();
      if (neuaufbau) clearTimeout(neuaufbau);
      if (neuladenUhr) clearInterval(neuladenUhr);
      anstossenRef.current = null;
      verbunden.current = false;
      document.removeEventListener("visibilitychange", beiSichtwechsel);
      clearInterval(netz);
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [router, source]);

  // Ueber den Merker und nicht direkt: `anstossen` entsteht erst im
  // Effekt, der die Leitung aufbaut.
  const request = useCallback(() => anstossenRef.current?.(), []);

  const wert = useMemo(() => ({ subscribe: anmelden, cover, request }), [anmelden, cover, request]);
  return <LiveKontext.Provider value={wert}>{children}</LiveKontext.Provider>;
}

/**
 * Fuer Stellen, die nach einer eigenen Action selbst
 * `router.refresh()` rufen (`useAction`, `DialogForm`): danach
 * aufrufen, dann entfaellt die Live-Auffrischung, die das Ereignis
 * derselben Action schon eingeplant hat. Ausserhalb des
 * `LiveProvider` tut es nichts.
 */
export function useLiveCover(): () => void {
  return useContext(LiveKontext)?.cover ?? keineDeckung;
}

function keineDeckung() {}

/**
 * Fuer Seiten, die ein PROTOKOLL zeichnen (`PROTOKOLL_MODELLE` in
 * `lib/live/modelle.ts`): deren Eintraege loesen keine allgemeine
 * Auffrischung aus, damit nicht jede Pruefung und jeder Zugriff
 * jeden Reiter im Haus neu zeichnet. Wer sie zeigt, stellt das hier
 * auf die Seite und frischt dann mit denselben Kanten auf wie bei
 * jedem anderen Ereignis (Abklingzeit, Mindestabstand, verdeckter
 * Reiter).
 *
 * Nur auf Seiten, deren Zeichnen die angemeldeten Modelle NICHT
 * selbst beschreibt — sonst frischt die Seite sich im Kreis auf.
 */
export function LiveRefreshOn({ models }: { models: readonly string[] }) {
  const request = useContext(LiveKontext)?.request;
  useLive(() => request?.(), models);
  return null;
}

/**
 * Fuer Ansichten, die ihre Daten SELBST halten und deshalb von
 * `router.refresh()` nichts mitbekommen.
 *
 * `modelle` schraenkt ein, worauf reagiert wird — die Glocke will
 * nichts von einer geaenderten Rechnung wissen. Ohne Angabe kommt
 * jedes Ereignis an.
 */
export function useLive(beiEreignis: Horcher, modelle?: readonly string[]) {
  const anmelden = useContext(LiveKontext)?.subscribe ?? null;
  const aktuell = useRef(beiEreignis);
  const schluessel = modelle ? [...modelle].sort().join(",") : "";

  // Der Rueckruf darf sich aendern, ohne die Anmeldung neu zu
  // knuepfen — sonst meldet sich die Ansicht bei jedem Zeichnen
  // ab und wieder an.
  useEffect(() => {
    aktuell.current = beiEreignis;
  }, [beiEreignis]);

  useEffect(() => {
    if (!anmelden) return;
    const gewuenscht = schluessel ? schluessel.split(",") : null;
    return anmelden((ereignis) => {
      if (gewuenscht && !gewuenscht.includes(ereignis.modell)) return;
      aktuell.current(ereignis);
    });
  }, [anmelden, schluessel]);
}
